'use client'

import { authenticateSession, sendOtp } from "../../lib/api";
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { useCallback, useState, useEffect, useRef } from "react"
import { useNavigate } from 'react-router-dom'; 
import { useAuth } from './authContext';


export default function LoginPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState<string>('');
    const [otp, setOtp] = useState<string>('');
    const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
    const [timer, setTimer] = useState<number>(60);
    const [error, setError] = useState<string>('');

    const { isAuthenticated, checkAuth } = useAuth();

    const otpInputRef = useRef<HTMLInputElement>(null);


    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);


    const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
    }, [])

    const handleSendOtp = useCallback(async () => {
        if(!email){
            setError('Failed to send OTP');
            return;
        }
        try {
            setIsOtpSent(true);
            setTimer(60);
            setError('');
            await sendOtp(email);
        } catch(error) {
            console.log(error);
            setError('Failed to send OTP');
            setIsOtpSent(false);
        } 
    }, [email])

    // Timer effect
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isOtpSent && timer > 0) {
            interval = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isOtpSent, timer]);

    useEffect(() => {
        if(isOtpSent){
            if (otpInputRef.current) {
                otpInputRef.current.focus();
            }
        }
    }, [isOtpSent])

    const handleOtpChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setOtp(e.target.value);
    }, [])

    const handleVerifyOtp = useCallback(async () => {
        if(!email || !otp){
            setError('User email or otp required');
            return;
        }
        try {
            const response = await authenticateSession(email, otp);
            if(!response) {
                setError('Invalid OTP, failed to authenticate user.');
            }
            localStorage.setItem("user", JSON.stringify(response.data));
            checkAuth();
        } catch(error) {
            console.log(error);
            setError('Failed to authenticate user.');
        } 
    }, [email, otp, checkAuth])

    return (
        <div className="flex flex-col items-center justify-center h-screen ">
            <Input type="email" placeholder="Email" value={email} onChange={handleEmailChange} className="mb-4 max-w-96"
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        handleSendOtp();
                    }
                }} 
            />
            { isOtpSent && (
                <div className="flex flex-col items-center justify-center">
                    <Input type="text" placeholder="OTP" value={otp} onChange={handleOtpChange} className="mb-2 max-w-96"
                    ref={otpInputRef}/>
                    <Button onClick={handleVerifyOtp} disabled={!otp} className="mb-2">Verify OTP</Button>
                </div>
            )}
            {
                isOtpSent ? (
                    <Button onClick={handleSendOtp} variant="link" disabled={isOtpSent && timer > 0}>
                        Resend OTP{timer > 0 ? ` in ${timer}s` : ''}
                    </Button>
                ) : (
                    <Button onClick={handleSendOtp}>
                        Send OTP
                    </Button>
                )  
            }
            {
                error && <p className="text-red-500">{error}</p>
            }
        </div>
    )
}   

